/**
 *
 *  Homepage
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 23/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import Slider from "react-slick";
import { FormattedMessage } from "react-intl";
import { Title, Meta } from "react-meta-elements";

// import Cookies from "js-cookie";

import { useAppState } from "../../state";

// Components
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import Button from "../Button/Button";
import ScrollToTopOnMount from "../ScrollToTopOnMount/ScrollToTopOnMount";
import PartnersScroller from "../Homepage/PartnersScroller/PartnersScroller";
import TrainerCard from "../Homepage/PartnersScroller/TrainerCard/TrainerCard";

// Hooks
import useForwardRef from "../../hooks/useForwardRef/useForwardRef";
import useTheme from "../ThemeProvider/useTheme/useTheme";
import { useEvent } from "../../hooks/useEvent/useEvent";
import useDeviceCapabilities from "../../hooks/useDeviceCapabilities/useDeviceCapabilities";

// Icons
import ArrowLeftIcon from "../../icons/ArrowLeftIcon";
import ArrowRightIcon from "../../icons/ArrowRightIcon";

// Utils
import { clientWidth, getLastUsedLocationStorageKey } from "../../utils";

// import { LAST_USED_LOCATION_STORAGE_KEY } from "../../constants";

// Images

const GymSlide = ({ gymData, onClick }) => {
  const handleClick = useCallback((e) => {
    onClick(e, gymData);
  }, []);

  return (
    <Link
      className=" content tbk-mx-2_ tbk-py-2_ tbk-h-60_ tbk-w-58_ tbk-flex tbk-flex-col tbk-rounded-2xl"
      to={`/gyms/${gymData.value}`}
      // onClick={handleClick}
    >
      <div className="tbk-relative">
        <img
          className="tbk--mt-4_ tbk-h-40 tbk-w-full tbk-rounded-2xl tbk-object-cover"
          src={gymData.photos[0]}
          alt={gymData.name}
        />
        <ul className="tb-disciplines-list tbk-absolute tbk-right-0 tbk-top-0 tbk-mt-1 tbk-mr-1 tbk-flex tbk-flex-col"></ul>
      </div>

      <div className="tbk-p-1">
        <div className="tbk-flex tbk-items-center">
          <img
            className="tbk-mr-2 tbk-h-7 tbk-w-7 tbk-shrink-0 tbk-object-contain"
            src={gymData.logo}
            // alt={trainer.name}
          />
          <div className="tbk-mr-3 tbk-min-w-0 tbk-grow">
            <div className="tbk-text-h3-subtitle tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-primary">
              {gymData.name}
            </div>
            <div className="tbk-text-small tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-grey">
              {gymData.address}
            </div>
          </div>
          <ArrowRightIcon className="tbk-shrink-0 tbk-text-coral" />
        </div>
      </div>
    </Link>
  );
};

const gymsSliderResponsiveSettings = [
  {
    breakpoint: 640,
    settings: {
      slidesToShow: 1,
      centerMode: true,
      centerPadding: "8px",
      initialSlide: 5,
    },
  },
  {
    breakpoint: 960,
    settings: {
      slidesToShow: 1,
      centerMode: true,
      centerPadding: "8px",
      initialSlide: 5,
    },
  },
  {
    breakpoint: 1280,
    settings: {
      slidesToShow: 2,
      centerMode: true,
      centerPadding: "8px",
      initialSlide: 2,
    },
  },
  {
    breakpoint: 1400,
    settings: {
      slidesToShow: 3,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "8px",
      initialSlide: 2,
    },
  },
  {
    breakpoint: 1600,
    settings: {
      slidesToShow: 3,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "8px",
      initialSlide: 2,
    },
  },
  {
    breakpoint: 1920,
    settings: {
      slidesToShow: 3,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "8px",
      initialSlide: 2,
    },
  },
  {
    breakpoint: 2560,
    settings: {
      slidesToShow: 3,
      slidesToScroll: 2,
      centerMode: true,
      centerPadding: "8px",
      initialSlide: 2,
    },
  },
  {
    breakpoint: 5000,
    settings: {
      slidesToShow: 3,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "8px",
      initialSlide: 2,
    },
  },
  { breakpoint: 10000, settings: "unslick" },
];

const getSliderResponsiveSettings = (length) => {
  return [
    {
      breakpoint: 960,
      settings: {
        slidesToShow: length,
        slidesToScroll: 1,
        // centerMode: true,
        // centerPadding: "8px",
        initialSlide: 0,
      },
    },
    {
      breakpoint: 1280,
      settings: {
        slidesToShow: length,
        slidesToScroll: 1,
        // centerMode: true,
        // centerPadding: "8px",
        initialSlide: 0,
      },
    },
    {
      breakpoint: 1400,
      settings: {
        slidesToShow: length,
        slidesToScroll: 1,
        // centerMode: true,
        // centerPadding: "8px",
        initialSlide: 0,
      },
    },
    {
      breakpoint: 5000,
      settings: {
        slidesToShow: length,
        slidesToScroll: 1,
        // centerMode: true,
        // centerPadding: "8px",
        initialSlide: 0,
      },
    },
  ];
};

const SliderPrevArrow = ({ className, style, onClick }) => (
  <button className={className} style={style} onClick={onClick}>
    <ArrowLeftIcon className="tbk-inline" />
  </button>
);

const SliderNextArrow = ({ className, style, onClick }) => (
  <button className={className} style={style} onClick={onClick}>
    <ArrowRightIcon className="tbk-inline" />
  </button>
);

export default function GymPage() {
  // const location = useLocation();
  const { GymId } = useParams();

  const { theme } = useTheme();

  const { user, availablePartners, gyms, loadAvailablePartners, loadGyms } =
    useAppState();

  const [_gym, setGym] = useState();

  const heroRef = React.useRef(null);
  const questionsSectionRef = useRef();

  let gymSliderRef = useRef(null);
  let gymSliderNavRef = useRef(null);

  const { supportsTouchCapableExperience } = useDeviceCapabilities(),
    [_supportsTouchCapableExperience, setSupportsTouchCapableExperience] =
      useState(supportsTouchCapableExperience ? 1 : 0);

  const slickInitialised = true;

  useEffect(() => {
    setSupportsTouchCapableExperience(supportsTouchCapableExperience ? 1 : 0);
  }, [supportsTouchCapableExperience]);

  const contentScrollerRef = useRef(null),
    [_contentScrollerRef, setContentScrollerRef] =
      useForwardRef(contentScrollerRef);

  const recalcReset = useEvent(() => {
    _contentScrollerRef &&
      _contentScrollerRef.recalc({
        reason: "reset",
      });
  });

  useEffect(() => {
    _contentScrollerRef && recalcReset();
  }, [recalcReset, _contentScrollerRef]);

  useEffect(
    () => {
      loadGyms({ withLogos: true, region: user ? user.region : "US" });
    },
    [user] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const recalcChildrenChangedEvent = useEvent(() => {
    _contentScrollerRef &&
      setTimeout(() =>
        _contentScrollerRef.recalc({
          reason: "children_changed",
        })
      );
  });

  useEffect(() => {
    _contentScrollerRef &&
      _supportsTouchCapableExperience > 0 &&
      recalcChildrenChangedEvent();
  }, [
    recalcChildrenChangedEvent,
    _contentScrollerRef,
    _supportsTouchCapableExperience,
  ]);

  const isUserLoggedIn = !!(user && user.id);

  React.useEffect(() => {
    if (!_gym) {
      return;
    }

    const params = {
      placeValue: _gym.value,
    };

    loadAvailablePartners(params, user ? user.region : "US"); // Will load coaches by "ENDURANCE" specialisation
  }, [user, _gym]);

  React.useEffect(() => {
    if (GymId && gyms && gyms.length) {
      const gym = gyms.find((v) => v.value == GymId);
      setGym(gym);
    }
  }, [GymId, gyms]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [_gym]);

  const handleViewAllCoachesClick = useCallback(
    (e) => {
      // e. preventDefault();

      const location = {
        label: _gym.address,
        lat: _gym.latitude,
        lng: _gym.longitude,
        placeId: _gym.placeId,
        value: {
          place: _gym.value,
          name: _gym.name,
          detail: _gym.detail,
        },
      };

      const storageKey = getLastUsedLocationStorageKey();

      localStorage.setItem(storageKey, JSON.stringify(location));
    },
    [user, _gym]
  );

  return (
    <>
      <ScrollToTopOnMount />
      <Meta
        name="description"
        content={`On-demand, in person, personal training in Los Angeles.`}
      />
      <Header
        isLoggedIn={isUserLoggedIn}
        heroRef={heroRef}
        onFaqClick={() =>
          questionsSectionRef.current.scrollIntoView({ behavior: "smooth" })
        }
      />

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pb-7 lg:tbk-pb-7">
        <div className="tbk-container tbk-px-3 lg:tbk-px-2">
          <div className="lg:tbk-gap- tbk-pt-3 lg:tbk-grid lg:tbk-grid-cols-2 lg:tbk-gap-8 lg:tbk-pt-7">
            <div className="tbk-mb-7 lg:tbk-mb-0">
              <div className="tbk-mb-2 tbk-flex tbk-items-center lg:tbk-mb-2">
                <img
                  className="tbk-mr-2 tbk-h-7 tbk-w-7 tbk-shrink-0 tbk-object-contain lg:tbk-h-36 lg:tbk-w-36"
                  src={_gym && _gym.logo}
                  // alt={trainer.name}
                />
                <div className="tbk-mr-3 tbk-min-w-0 tbk-grow">
                  <div className="tbk-text-h3-subtitle lg:tbk-text-h2-black tbk-overflow-hidden_ tbk-text-ellipsis_ tbk-whitespace-nowrap_ tbk-text-primary lg:tbk-mb-1 lg:!tbk-leading-tight">
                    {_gym && _gym.name}
                  </div>
                  <div className="tbk-text-small lg:tbk-text-main tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-grey">
                    {_gym && _gym.address}
                  </div>
                </div>
              </div>

              <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
                {_gym && _gym.description}
              </p>

              <div className="tbk-text-center_ lg:tbk-text-left">
                <Link
                  to={isUserLoggedIn || true ? "/partners" : "/become-a-member"}
                  onClick={handleViewAllCoachesClick}
                >
                  <Button
                    size={"md"}
                    className="tbk-mt-3_ tbk-mb-3_ tbk-inline-flex tbk-pl-5 tbk-pr-5 lg:tbk-mt-0 lg:tbk-mb-0"
                    endIcon={<ArrowRightIcon className="tbk-ml-0.5" />}
                  >
                    View All{" "}
                    <FormattedMessage id="Trainers" defaultMessage="Trainers" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl tbk-bg-home-our-coaches-mobile lg:tbk-h-auto lg:tbk-bg-none">
              {slickInitialised && _gym && _gym.photos.length ? (
                <>
                  <Slider
                    ref={gymSliderRef}
                    className="gyms-carousel tbk--ml-2_ tbk--mr-2_"
                    // dots={true}
                    // dotsClass={"slick-dots tbk-pl-3"}
                    // arrows
                    arrows={false}
                    // autoplay
                    fade
                    autoplaySpeed={4000}
                    pauseOnFocus
                    infinite={true}
                    speed={500}
                    slidesToShow={1}
                    slidesToScroll={1}
                    initialSlide={0}
                    asNavFor={gymSliderNavRef.current}
                  >
                    {_gym.photos.slice(0, 5).map((gymPhoto) => (
                      <div className=" content tbk-mx-2_ tbk-py-2_ tbk-h-60_ tbk-w-58_ tbk-flex tbk-flex-col tbk-rounded-2xl">
                        {gymPhoto ? (
                          <div className="tbk-relative">
                            <img
                              className="tbk--mt-4_ tbk-h-70 tbk-w-full tbk-rounded-2xl tbk-object-cover lg:tbk-h-84"
                              src={gymPhoto}
                              // alt={trainer.name}
                            />
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </Slider>
                  <Slider
                    ref={gymSliderNavRef}
                    className="gyms-carousel-nav tbk--ml-2_ tbk--mr-2_"
                    autoplaySpeed={4000}
                    pauseOnFocus
                    infinite={true}
                    speed={500}
                    slidesToShow={5}
                    slidesToScroll={1}
                    initialSlide={0}
                    asNavFor={gymSliderRef.current}
                    // asNavFor={nav1}
                    focusOnSelect
                    responsive={getSliderResponsiveSettings(
                      5 || _gym.photos.length
                    )}
                  >
                    {_gym.photos.slice(0, 5).map((gymPhoto) => (
                      <div className=" content tbk-px-2_ tbk-py-2_ tbk-h-60_ tbk-w-58_ tbk-flex tbk-flex-col tbk-rounded-2xl">
                        {gymPhoto ? (
                          <div className="tbk-relative">
                            <img
                              className="tbk--mt-4_ tbk-h-6 tbk-w-full tbk-rounded-2xl tbk-object-cover md:tbk-h-24"
                              src={gymPhoto}
                              // alt={trainer.name}
                            />
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </Slider>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {availablePartners && availablePartners.length ? (
        <div className="tbk-relative tbk-bg-basic-white tbk-pt-7 tbk-pb-7 lg:tbk-pt-0 lg:tbk-pb-0">
          <div className="tbk-container tbk-z-1 tbk-mx-auto lg:tbk-px-0 lg:tbk-pb-9">
            <div className="tbk-relative tbk-mb-5 tbk-text-center">
              <h3 className="tbk-text-h3-subtitle lg:tbk-text-title-bold tbk-inline-block tbk-bg-basic-white tbk-px-2 tbk-text-center tbk-text-blue-grey">
                <FormattedMessage id="Trainers" defaultMessage="Trainers" /> in
                the Gym
              </h3>
              <hr className="tbk-mb-0 tbk--mt-2 tbk-border-blue-grey-light" />
            </div>

            <div className="tbk-h-68_ tbk-mb-4 lg:tbk-mb-2">
              {availablePartners && availablePartners.length ? (
                <PartnersScroller
                  partners={availablePartners}
                  renderPartner={({
                    info: partnerInfo,
                    index,
                    contentScrollerApi,
                    onCardVisibleChange,
                  }) => (
                    <Link to={`/partners/${partnerInfo.id}`}>
                      <TrainerCard
                        trainer={partnerInfo}
                        key={partnerInfo.id}
                        itemKey={index}
                        contentScrollerApi={contentScrollerApi}
                        onCardVisibleChange={onCardVisibleChange}
                        // onCardClick={handleCardClick}
                      />
                    </Link>
                  )}
                />
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <div
        id="gyms"
        className="tbk-relative tbk-bg-basic-white tbk-pt-4 tbk-pb-24 lg:tbk-pt-9 lg:tbk-pb-7"
      >
        <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-px-2 lg:tbk-px-8 lg:tbk-pb-9">
          <div className="tbk-relative tbk-mb-3 tbk-text-center lg:tbk-mb-8">
            <h3 className="tbk-text-h3-subtitle lg:tbk-text-title-bold tbk-inline-block tbk-bg-basic-white tbk-px-2 tbk-text-center tbk-text-blue-grey">
              Gym Partners
            </h3>
            <hr className="tbk-mb-0 tbk--mt-2 tbk-border-blue-grey-light" />
          </div>

          <p className="tbk-text-small tbk-mb-4 tbk-text-center tbk-text-blue-grey lg:tbk-hidden">
            Access preferred gym locations
            <br /> with your TruBe{" "}
            <FormattedMessage id="Trainer" defaultMessage="Trainer" />
          </p>

          {slickInitialised && gyms && gyms.length ? (
            <Slider
              className="home-gyms-carousel slick-slider-shadow-fix tbk--ml-2 tbk--mr-2"
              dots={true}
              dotsClass={"slick-dots tbk-pl-3"}
              // arrows
              arrows={clientWidth() >= theme.responsive.breakpoints.xl}
              autoplay
              autoplaySpeed={4000}
              pauseOnFocus
              infinite={true}
              speed={500}
              slidesToShow={1}
              slidesToScroll={1}
              initialSlide={0}
              swipe={clientWidth() < theme.responsive.breakpoints.lg}
              useTransform={false}
              responsive={gymsSliderResponsiveSettings}
              nextArrow={<SliderNextArrow className="" />}
              prevArrow={<SliderPrevArrow className="" />}
            >
              {gyms.map((gym) => (
                <GymSlide gymData={gym} />
              ))}
            </Slider>
          ) : null}
        </div>
      </div>

      <Footer />
    </>
  );
}
